import PropTypes from 'prop-types';
import React from 'react';
import { Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import * as Icons from './SidebarLogos';

/**
 * NavItem component for rendering navigation items in a sidebar
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.item - Navigation item configuration
 * @param {string} props.item.icon - Icon name from SidebarLogos
 * @param {string} props.item.path - URL path or external link
 * @param {string} props.item.label - Display text for the navigation item
 * @param {boolean} [props.item.external] - If true, opens link in new tab
 * @param {boolean} [props.isActive=false] - Whether the nav item is currently active
 * @param {boolean} [props.sidebarShow=true] - Whether the sidebar is expanded
 * @returns {React.ReactElement} A navigation item component
 *
 * @example
 * const item = {
 *   icon: 'HomeLogo',
 *   path: '/dashboard',
 *   label: 'Dashboard',
 *   external: false
 * };
 *
 * <NavItem
 *   item={item}
 *   isActive={true}
 *   sidebarShow={false}
 * />
 */
const NavItem = ({ item, isActive = false, sidebarShow = true }) => {
  const Icon = Icons[item.icon] || Icons.HomeLogo;
  const linkProps = item.external
    ? { as: 'a', href: item.path, target: '_blank', rel: 'noopener noreferrer' }
    : { as: Link, to: item.path };

  const content = (
    <>
      <span className="nav-icon">
        <Icon />
      </span>
      <span className="toggle-name">{item.label}</span>
    </>
  );

  const navLink = (
    <Nav.Link {...linkProps} active={isActive}>
      {content}
    </Nav.Link>
  );

  return (
    <Nav.Item as="li" key={item.path}>
      {sidebarShow ? (
        navLink
      ) : (
        <OverlayTrigger
          placement="right"
          overlay={<Tooltip id={`nav-${item.label.toLowerCase()}`}>{item.label}</Tooltip>}
        >
          {navLink}
        </OverlayTrigger>
      )}
    </Nav.Item>
  );
};

NavItem.propTypes = {
  item: PropTypes.shape({
    icon: PropTypes.string,
    path: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    external: PropTypes.bool,
  }).isRequired,
  isActive: PropTypes.bool,
  sidebarShow: PropTypes.bool,
};

export default NavItem;
